import axios from "axios";
import { ACCESS_KEY, REFRESH_KEY, USER_KEY, saveSession, clearSession } from "@/lib/auth";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 interceptor with refresh token rotation (single retry)
let refreshPromise = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || config?._retry) throw error;
    // Do not try to refresh on the auth endpoints themselves
    const url = (config?.url || "").toString();
    if (url.includes("/auth/login") || url.includes("/auth/refresh") ||
        url.includes("/auth/register") || url.includes("/auth/2fa/confirm")) {
      throw error;
    }
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) {
      clearSession();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
      throw error;
    }
    try {
      if (!refreshPromise) {
        refreshPromise = axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refreshToken })
          .then((res) => {
            const newAccess = res.data.access_token;
            localStorage.setItem(ACCESS_KEY, newAccess);
            return newAccess;
          })
          .finally(() => { refreshPromise = null; });
      }
      const newAccess = await refreshPromise;
      config._retry = true;
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${newAccess}`;
      return api.request(config);
    } catch (e) {
      clearSession();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
      throw error;
    }
  }
);

/**
 * Convierte un detail de error de FastAPI en string mostrable.
 *
 * Acepta:
 *   - AxiosError completo (recomendado; usa status para mensajes específicos)
 *   - string
 *   - array de errores de Pydantic 422 [{loc, msg, type}, ...]
 *   - objeto con `.message` (patrón custom `{code, message, retry_after_seconds}`)
 *   - objeto con `.msg` (Pydantic single field error)
 *   - objeto con `.detail` (respuesta anidada)
 *   - null / undefined
 *
 * Nunca devuelve `[object Object]`.
 */
export function formatApiError(input) {
  // Caso 1: AxiosError con response
  if (input && typeof input === "object" && input.response) {
    const status = input.response.status;
    const detail = input.response.data?.detail;
    // Mensajes específicos por status (fallback si el detail no es amable)
    const statusMessages = {
      422: "Revisa los campos del formulario.",
      429: "Estás enviando muchas solicitudes. Intenta en unos minutos.",
      500: "Ocurrió un error en el servidor. Intenta de nuevo.",
      502: "El servicio no responde temporalmente.",
      503: "El servicio no está disponible temporalmente.",
    };
    // Si hay detail, intenta usarlo; si sale como fallback genérico, usa el
    // mensaje por status si aplica.
    const detailMsg = _detailToString(detail);
    if (detailMsg && detailMsg !== "Ocurrió un error. Intenta de nuevo.") {
      return detailMsg;
    }
    return statusMessages[status] || detailMsg;
  }
  // Caso 2: detail directo (retrocompat con consumidores viejos)
  return _detailToString(input);
}

function _detailToString(detail) {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  // Array de errores de Pydantic 422
  if (Array.isArray(detail)) {
    const parts = detail.map((e) => {
      if (typeof e === "string") return e;
      if (e && typeof e.msg === "string") {
        // Pydantic 422: incluye el campo si existe
        const loc = Array.isArray(e.loc) ? e.loc.slice(-1)[0] : null;
        return loc ? `${loc}: ${e.msg}` : e.msg;
      }
      return null;
    }).filter(Boolean);
    if (parts.length) return parts.join(" · ");
    return "Revisa los campos del formulario.";
  }
  // Objeto: prioridad `message` (patrón custom Fase 14+) → `msg` (Pydantic) →
  // `detail` recursivo → primer valor string.
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (detail.detail) return _detailToString(detail.detail);
    // Fallback: primer valor string encontrado
    for (const v of Object.values(detail)) {
      if (typeof v === "string") return v;
    }
    return "Ocurrió un error. Intenta de nuevo.";
  }
  return String(detail);
}
